/**
 * Sidebar menu catalog and the per-menu-item RBAC model.
 *
 * Permissions are expressed PER MENU ITEM (none/view/edit/delete). The menu item is the
 * unit an admin grants, giving granular control over what each member sees and can do.
 *  - Sidebar visibility: an item shows when its effective level is >= `view`. A section
 *    header shows only when at least one of its items is visible.
 *  - Backend access: each item that maps to a backend RBAC `area` contributes its level
 *    to that area; the effective area permission is the most-permissive level across all
 *    items mapping to it. This keeps the area-based API guards working unchanged.
 *
 * Items with no `area` are visibility-only: admin-only pages (still gated by `@RequireAdmin`
 * on the backend) and composite pages (e.g. Forecast) that read several areas.
 *
 * The keys here MUST match the `key` on each item in apps/frontend/src/navigation.ts.
 */
import { RBAC_AREAS, RBAC_LEVELS, RBAC_LEVEL_RANK, maxLevel, } from '../rbac.js';
export const MENU_ITEMS = [
    // Sales
    { key: 'prospects', label: 'Prospects', section: 'Sales', area: 'customers' },
    { key: 'opportunities', label: 'Opportunities', section: 'Sales', area: 'sales' },
    { key: 'quotes', label: 'Quotes', section: 'Sales', area: 'sales' },
    { key: 'onboarding', label: 'Onboarding', section: 'Sales', area: 'sales' },
    // Delivery
    // Work Forecast is a visibility-only count view (no area); the endpoint requires only auth.
    { key: 'work-forecast', label: 'Work Forecast', section: 'Delivery' },
    { key: 'customers', label: 'Customers', section: 'Delivery', area: 'customers' },
    { key: 'contacts', label: 'Contacts', section: 'Delivery', area: 'customers' },
    { key: 'projects', label: 'Projects', section: 'Delivery', area: 'projects' },
    { key: 'support-tickets', label: 'Support Tickets', section: 'Delivery', area: 'supportTickets' },
    { key: 'project-tickets', label: 'Project Tickets', section: 'Delivery', area: 'projectTickets' },
    { key: 'timesheets', label: 'Timesheets', section: 'Delivery', area: 'time' },
    { key: 'time-entries', label: 'Time Entries', section: 'Delivery', area: 'time' },
    { key: 'approvals', label: 'Approvals', section: 'Delivery', area: 'time' },
    // Billing
    { key: 'generate-invoices', label: 'Generate Invoices', section: 'Billing', area: 'invoices' },
    { key: 'invoices', label: 'Invoices', section: 'Billing', area: 'invoices' },
    { key: 'agreements', label: 'Agreements', section: 'Billing', area: 'agreements' },
    { key: 'qbo-batch', label: 'Send to QuickBooks', section: 'Billing', area: 'invoices' },
    { key: 'billing-audit', label: 'Billing Audit', section: 'Billing', area: 'invoices' },
    // Insights
    { key: 'internal-reports', label: 'Internal Reports', section: 'Insights', area: 'reports' },
    { key: 'customer-reports', label: 'Customer Reports', section: 'Insights', area: 'reports' },
    // Dashboards is visibility-only: every card declares its own RBAC area and is withheld
    // server-side from viewers who cannot read it. `edit` additionally allows creating and
    // editing SHARED dashboards (personal ones need only `view`).
    { key: 'dashboards', label: 'Dashboards', section: 'Insights' },
    // Insights Studio (AI analytics) reads time data across ALL members, so it is granted
    // explicitly per role; the backend checks this menu permission on every endpoint.
    { key: 'insights-studio', label: 'Insights Studio', section: 'Insights' },
    // Forecast reads several areas; visibility-only (grant the underlying items for data).
    { key: 'forecast', label: 'Forecast', section: 'Insights' },
    // Administration
    { key: 'members', label: 'Members', section: 'Administration' },
    { key: 'roles', label: 'Roles', section: 'Administration' },
    { key: 'products', label: 'Products', section: 'Administration', area: 'products' },
    {
        key: 'service-categories',
        label: 'Service Categories',
        section: 'Administration',
        area: 'products',
    },
    { key: 'billing-classes', label: 'Billing Classes', section: 'Administration', area: 'products' },
    { key: 'ticket-types', label: 'Ticket Types', section: 'Administration' },
    { key: 'ticket-groups', label: 'Ticket Groups', section: 'Administration' },
    { key: 'ticket-statuses', label: 'Ticket Statuses', section: 'Administration' },
    { key: 'customer-types', label: 'Customer Types', section: 'Administration' },
    { key: 'agreement-subtypes', label: 'Agreement Subtypes', section: 'Administration' },
    { key: 'invoice-templates', label: 'Invoice Templates', section: 'Administration' },
    // Quote templates are managed by sales admins, not only PSA admins: the API uses the
    // `sales` area so a role granted this item can create/edit/delete templates.
    { key: 'quote-templates', label: 'Quote Templates', section: 'Administration', area: 'sales' },
    // Integrations (admin-only at the API; visibility-only)
    { key: 'api-users', label: 'API Users', section: 'Integrations' },
    { key: 'excel-access', label: 'Excel Access', section: 'Integrations' },
    { key: 'email-settings', label: 'Email Settings', section: 'Integrations' },
    { key: 'connectwise', label: 'ConnectWise', section: 'Integrations' },
    { key: 'quickbooks', label: 'QuickBooks', section: 'Integrations' },
    { key: 'clickup', label: 'ClickUp', section: 'Integrations' },
    { key: 'microsoft-graph', label: 'Microsoft Graph', section: 'Integrations' },
    { key: 'mailerlite', label: 'MailerLite', section: 'Integrations' },
    { key: 'catalyst-portal', label: 'Catalyst Portal', section: 'Integrations' },
    { key: 'connectwise-cpq', label: 'ConnectWise CPQ', section: 'Integrations' },
    { key: 'ai-assistant', label: 'AI Assistant', section: 'Integrations' },
];
/** Every valid menu key (for validation + membership tests). */
export const MENU_ITEM_KEYS = MENU_ITEMS.map((m) => m.key);
const MENU_KEY_SET = new Set(MENU_ITEM_KEYS);
const LEVEL_SET = new Set(RBAC_LEVELS);
/** Returns the granted level for a menu key (`none` when absent). */
export function menuLevelOf(perms, key) {
    return perms[key] ?? 'none';
}
/** Whether a principal may SEE a menu item (admins always can; otherwise level >= view). */
export function canSeeMenuItem(perms, key, isAdmin) {
    if (isAdmin)
        return true;
    return RBAC_LEVEL_RANK[menuLevelOf(perms, key)] >= RBAC_LEVEL_RANK.view;
}
/**
 * Normalizes a raw map (e.g. parsed from storage) into a clean {@link MenuPermissions}:
 * only known keys with a valid level other than `none` are kept.
 */
export function normalizeMenuPermissions(input) {
    const out = {};
    for (const [key, value] of Object.entries(input)) {
        if (!MENU_KEY_SET.has(key))
            continue;
        if (typeof value !== 'string' || !LEVEL_SET.has(value) || value === 'none')
            continue;
        out[key] = value;
    }
    return out;
}
/** Converts a DTO list into a {@link MenuPermissions} map (known keys, non-none levels). */
export function menuPermissionsFromList(list) {
    const out = {};
    for (const { key, level } of list) {
        if (!MENU_KEY_SET.has(key) || !LEVEL_SET.has(level) || level === 'none')
            continue;
        out[key] = level;
    }
    return out;
}
/** Converts a {@link MenuPermissions} map into a DTO list in catalog order. */
export function menuPermissionsToList(perms) {
    const out = [];
    for (const item of MENU_ITEMS) {
        const level = perms[item.key];
        if (level && level !== 'none')
            out.push({ key: item.key, level });
    }
    return out;
}
/**
 * Combines the per-role menu-permission maps for a principal into one effective map:
 * the most-permissive level wins per key (matching the area RBAC model).
 */
export function combineMenuPermissions(roleMaps) {
    const out = {};
    for (const map of roleMaps) {
        for (const [key, level] of Object.entries(map)) {
            if (!MENU_KEY_SET.has(key) || level === 'none')
                continue;
            out[key] = out[key] ? maxLevel(out[key], level) : level;
        }
    }
    return out;
}
/**
 * Derives effective area permissions from a principal's menu permissions: each area gets
 * the most-permissive level across all menu items that map to it. Items without an `area`
 * (admin-only / composite pages) contribute nothing.
 */
export function deriveAreaPermissions(perms) {
    const effective = RBAC_AREAS.reduce((acc, area) => {
        acc[area] = 'none';
        return acc;
    }, {});
    for (const item of MENU_ITEMS) {
        if (!item.area)
            continue;
        const level = perms[item.key];
        if (!level || level === 'none')
            continue;
        effective[item.area] = maxLevel(effective[item.area], level);
    }
    // The `tickets` umbrella area (used by coarse read gates: forecast, OData, assistant, audit
    // history) is the most-permissive of the two per-origin areas — a member with either grant
    // can reach ticket-adjacent surfaces, while write/delete is enforced per-origin downstream.
    effective.tickets = maxLevel(effective.supportTickets, effective.projectTickets);
    return effective;
}
//# sourceMappingURL=menu.js.map